import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { AlertTriangle, Heart, Scale, Shield, Stethoscope, ClipboardX } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const DisclaimerModal = ({ onAccept, onClose }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!acknowledged) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/user/accept-disclaimer`);
      onAccept();
    } catch (error) {
      console.error('Error accepting disclaimer:', error);
      onAccept(); // Continue anyway
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="disclaimer-modal">
      <Card className="glass max-w-3xl w-full p-6 sm:p-10 border-2 border-blood-700 my-8">
        <div className="text-center mb-7">
          <div className="flex justify-center mb-4">
            <div className="bg-blood-900/30 p-4 rounded-full border-2 border-blood-700">
              <Shield className="w-12 h-12 text-blood-400" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-blood-400 mb-2 tracking-wider">
            MEDICAL DISCLAIMER & LIABILITY WAIVER
          </h1>
          <p className="text-stone-400 uppercase tracking-widest text-xs">
            Read in full. Required to use Anvil &amp; Ash.
          </p>
        </div>

        <div className="space-y-4 mb-6 text-stone-300 text-sm leading-relaxed">
          {/* Not a medical professional */}
          <div className="glass p-4 rounded-lg border border-blood-800">
            <div className="flex items-start gap-3">
              <Stethoscope className="w-6 h-6 text-blood-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-blood-400 mb-1.5 tracking-wide">
                  WE ARE NOT MEDICAL PROFESSIONALS
                </h3>
                <p>
                  Anvil &amp; Ash is operated by individuals who are <strong className="text-white">not physicians,
                  licensed medical professionals, registered dietitians, physical therapists, or certified
                  personal trainers</strong>. Nothing provided in this app constitutes medical advice, diagnosis,
                  treatment, prescription, or therapy of any kind.
                </p>
              </div>
            </div>
          </div>

          {/* Consult a doctor */}
          <div className="glass p-4 rounded-lg border border-blood-800">
            <div className="flex items-start gap-3">
              <Heart className="w-6 h-6 text-blood-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-blood-400 mb-1.5 tracking-wide">
                  CONSULT YOUR DOCTOR BEFORE STARTING
                </h3>
                <p>
                  Before beginning any exercise or nutrition program, <strong className="text-white">consult
                  a qualified physician</strong>, especially if you have any pre-existing medical condition,
                  cardiovascular issue, injury, are pregnant, postpartum, recovering from surgery, taking
                  prescription medication, or have been sedentary. The information here is not a substitute
                  for professional medical evaluation or care.
                </p>
              </div>
            </div>
          </div>

          {/* Stop signs */}
          <div className="glass p-4 rounded-lg border border-amber-700/60 bg-amber-950/10">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-amber-300 mb-1.5 tracking-wide">
                  STOP IMMEDIATELY IF YOU FEEL
                </h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Chest pain, pressure, or tightness</li>
                  <li>Shortness of breath beyond expected exertion</li>
                  <li>Dizziness, lightheadedness, or fainting</li>
                  <li>Nausea, severe fatigue, or cold sweats</li>
                  <li>Sharp pain, joint pain, or pain that persists</li>
                </ul>
                <p className="mt-2 font-semibold text-white">
                  Seek immediate medical attention. Do not "push through" warning signs.
                </p>
              </div>
            </div>
          </div>

          {/* Tracker only */}
          <div className="glass p-4 rounded-lg border border-blood-800">
            <div className="flex items-start gap-3">
              <ClipboardX className="w-6 h-6 text-blood-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-blood-400 mb-1.5 tracking-wide">
                  WHAT ANVIL &amp; ASH IS — AND IS NOT
                </h3>
                <p className="mb-2">
                  Anvil &amp; Ash is a <strong className="text-white">general-purpose self-logging tool</strong>
                  {' '}for personal fitness tracking. It is <strong className="text-white">NOT</strong>:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>A medical, healthcare, or diagnostic service</li>
                  <li>A substitute for professional medical advice, evaluation, or treatment</li>
                  <li>Physical therapy, rehabilitation, or post-injury care</li>
                  <li>Nutrition therapy, medical nutrition therapy, or dietitian counseling</li>
                  <li>A certified personal training program</li>
                  <li>A weight-loss, weight-gain, or body-composition guarantee</li>
                  <li>A device or tool for measuring or recording exercise scientifically</li>
                  <li>Approved by the FDA, AMA, or any medical or regulatory body</li>
                </ul>
                <p className="mt-2">
                  All workouts, nutrition data, calorie burn estimates, AI-generated suggestions,
                  recipe scans, and recovery routines are <strong className="text-white">approximations
                  for personal informational use only</strong>. We make no claim or guarantee about
                  accuracy, completeness, suitability, or fitness for any particular purpose.
                </p>
              </div>
            </div>
          </div>

          {/* Assumption of risk + waiver */}
          <div className="glass p-4 rounded-lg border border-stone-700 bg-stone-950/60">
            <div className="flex items-start gap-3">
              <Scale className="w-6 h-6 text-blood-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-blood-400 mb-1.5 tracking-wide">
                  ASSUMPTION OF RISK &amp; RELEASE OF LIABILITY
                </h3>
                <p className="mb-2">
                  By using Anvil &amp; Ash, you knowingly and voluntarily assume <strong className="text-white">
                  full responsibility and all risk</strong> of injury, illness, death, property damage,
                  or any adverse outcome resulting from your use of the app or any activity inspired
                  by it. You agree that you are participating at your own risk.
                </p>
                <p>
                  You release, waive, and hold harmless Anvil &amp; Ash, its owners, operators,
                  contributors, employees, contractors, and affiliates from any and all claims,
                  liability, damages, costs, or losses, whether direct, indirect, incidental,
                  consequential, or otherwise, arising from your use of the app — including but
                  not limited to physical injury, health complications, loss of data, or financial loss.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Required checkbox */}
        <div className="border-t border-stone-800 pt-4 mb-5">
          <label
            htmlFor="disclaimer-ack"
            className="flex items-start gap-3 cursor-pointer select-none"
          >
            <Checkbox
              id="disclaimer-ack"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(!!v)}
              className="mt-0.5 border-stone-500 data-[state=checked]:bg-blood-700 data-[state=checked]:border-blood-600"
              data-testid="disclaimer-ack-checkbox"
            />
            <span className="text-stone-200 text-sm">
              I have read, understood, and agree to the <strong className="text-white">Medical Disclaimer
              &amp; Liability Waiver</strong> in full. I confirm I am <strong className="text-white">
              at least 18 years of age</strong> (or have a parent/guardian's consent if 13–17) and
              that I will consult a qualified physician before starting any exercise or nutrition
              program. I understand Anvil &amp; Ash does not provide medical advice and that I use
              it entirely at my own risk.
            </span>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleAccept}
            disabled={!acknowledged || submitting}
            className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-4 text-base tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="disclaimer-accept-btn"
          >
            {submitting ? 'SAVING…' : 'I AGREE & ACCEPT'}
          </Button>
          {onClose && (
            <Button
              onClick={onClose}
              variant="outline"
              disabled={submitting}
              className="sm:w-40 glass border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800 hover:text-white font-semibold"
              data-testid="disclaimer-cancel-btn"
            >
              CANCEL
            </Button>
          )}
        </div>

        <p className="text-center text-stone-500 text-xs mt-5">
          You can review this disclaimer anytime from Settings → Legal.
        </p>
      </Card>
    </div>
  );
};

export default DisclaimerModal;
